// ================================================
// CLARITY REPORTING — REPORT PAGE
// Full report viewer with keyword tables
// ================================================

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/common/Sidebar';
import api from '../services/api';

function ChangeBadge({ change }) {
  if (!change || change === 0) return <span className="change-badge none">—</span>;
  if (change > 0) return <span className="change-badge up">▲ {change}</span>;
  return <span className="change-badge down">▼ {Math.abs(change)}</span>;
}

function RankBadge({ position }) {
  if (!position) return <span className="text-muted">—</span>;
  return <span className="rank-badge">{position}</span>;
}

export default function ReportPage() {
  const { reportId } = useParams();
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all');

  useEffect(() => { fetchReport(); }, [reportId]);

  async function fetchReport() {
    try {
      const { data } = await api.get(`/reports/${reportId}`);
      setReport(data);
    } catch {
      toast.error('Report not found');
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">
        <div className="loading">Loading report&hellip;</div>
      </main>
    </div>
  );

  if (!report) return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">
        <div className="page-container">
          <div className="empty-state">
            <h3>Report Not Found</h3>
            <Link to="/dashboard" className="btn btn-primary">Back to Dashboard</Link>
          </div>
        </div>
      </main>
    </div>
  );

  const { data: reportData } = report;
  const keywords       = reportData?.keywords || [];
  const growthKeywords = reportData?.growthKeywords || [];

  const filteredKeywords = filter === 'all' ? keywords :
    keywords.filter(k => k.status === filter);

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">

        <div className="page-header">
          <div className="flex-between">
            <div>
              <h1>{report.display_name || report.domain}</h1>
              <div style={{ fontSize: 13, color: 'var(--color-gray-500)', paddingBottom: 20 }}>
                Weekly Report — {new Date(report.report_date).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                })}
                {reportData?.website?.gsc_connected && (
                  <span style={{ marginLeft: 12, color: 'var(--color-success-text)', fontWeight: 600 }}>
                    ✓ Google Search Console
                  </span>
                )}
              </div>
            </div>
            <Link to="/dashboard" className="btn btn-outline btn-sm">
              ← Back to Dashboard
            </Link>
          </div>
        </div>

        <div className="page-container">

          {/* Summary stats */}
          <div className="stat-grid">
            <div className="stat-box">
              <div className="stat-value">{report.total_keywords}</div>
              <div className="stat-label">Keywords</div>
            </div>
            <div className="stat-box success">
              <div className="stat-value">▲ {report.keywords_improved}</div>
              <div className="stat-label">Improved</div>
            </div>
            <div className="stat-box concern">
              <div className="stat-value">{report.keywords_concern}</div>
              <div className="stat-label">Concerns</div>
            </div>
            <div className="stat-box problem">
              <div className="stat-value">{report.keywords_problem}</div>
              <div className="stat-label">Problems</div>
            </div>
          </div>

          {/* Keyword filter tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--color-black)' }}>
            {[
              { key: 'all',     label: 'All' },
              { key: 'problem', label: `Problems (${report.keywords_problem})` },
              { key: 'concern', label: `Concerns (${report.keywords_concern})` },
              { key: 'improved',label: `Improved (${report.keywords_improved})` }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                style={{
                  padding: '10px 20px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  border: 'none',
                  borderBottom: filter === tab.key ? '2px solid var(--color-black)' : '2px solid transparent',
                  marginBottom: -2,
                  background: 'none',
                  cursor: 'pointer',
                  color: filter === tab.key ? 'var(--color-black)' : 'var(--color-gray-500)'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Keyword rankings table */}
          <div className="card" style={{ padding: 0, marginBottom: 32 }}>
            <div style={{ padding: '20px 24px', borderBottom: 'var(--border-light)' }}>
              <span className="card-title">Keyword Rankings</span>
            </div>
            {filteredKeywords.length === 0 ? (
              <div className="empty-state">
                <p>No keywords match this filter.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th style={{ textAlign: 'center' }}>Rank</th>
                    <th style={{ textAlign: 'center' }}>Change</th>
                    {reportData?.website?.gsc_connected && <>
                      <th style={{ textAlign: 'right' }}>Impressions</th>
                      <th style={{ textAlign: 'right' }}>CTR</th>
                    </>}
                  </tr>
                </thead>
                <tbody>
                  {filteredKeywords.map((kw, i) => (
                    <tr key={i} className={`status-${kw.status}`}>
                      <td>{kw.keyword}</td>
                      <td style={{ textAlign: 'center' }}>
                        <RankBadge position={kw.position} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <ChangeBadge change={kw.change} />
                      </td>
                      {reportData?.website?.gsc_connected && <>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                          {kw.impressions?.toLocaleString() || '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                          {kw.ctr ? `${kw.ctr}%` : '—'}
                        </td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Growth Opportunities */}
          {growthKeywords.length > 0 && (
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '20px 24px', borderBottom: 'var(--border-light)' }}>
                <span className="card-title">Growth Opportunities</span>
              </div>
              <div style={{ padding: '12px 24px', borderBottom: 'var(--border-light)', background: 'var(--color-gray-50)' }}>
                <p style={{ fontSize: 13, color: 'var(--color-gray-500)', margin: 0 }}>
                  Keywords your competitors rank for that your site is currently missing.
                  These represent untapped traffic opportunities.
                </p>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th style={{ textAlign: 'right' }}>Search Volume</th>
                    <th>Found On</th>
                    <th style={{ textAlign: 'center' }}>Their Rank</th>
                  </tr>
                </thead>
                <tbody>
                  {growthKeywords.map((gk, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{gk.keyword}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                        {gk.search_volume?.toLocaleString() || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
                        {gk.found_on_competitor}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="rank-badge">#{gk.competitor_position || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
